import React, { useState } from 'react';
import { doc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase-init';

export default function MarkIncompleteForm({ isOpen, onClose, job, installerName, onSuccess }) {
  const [reason, setReason] = useState('');
  const [continuationDate, setContinuationDate] = useState('');
  const [notes, setNotes] = useState('');
  const [workCompleted, setWorkCompleted] = useState('');
  const [workRemaining, setWorkRemaining] = useState('');
  const [customerInformed, setCustomerInformed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reasons = [
    { value: '', label: 'Select a reason (optional)' },
    { value: 'ran_out_of_time', label: '⏰ Ran out of time / Got too dark' },
    { value: 'weather', label: '🌧️ Weather (rain, snow, wind, too cold)' },
    { value: 'personal_emergency', label: '🚨 Personal emergency / Had to leave' },
    { value: 'missing_materials', label: '🔧 Missing materials / Wrong product' },
    { value: 'site_not_ready', label: '🏗️ Site not ready / Customer issue' },
    { value: 'job_bigger', label: '📦 Job bigger than expected' },
    { value: 'equipment_issue', label: '🚗 Equipment issue / Vehicle problem' },
    { value: 'personal_appointment', label: '📅 Personal appointment / Family commitment' },
    { value: 'holiday', label: '🎉 Holiday / Special event' },
    { value: 'not_feeling_well', label: '🏥 Not feeling well' },
    { value: 'other', label: '📝 Other' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const jobRef = doc(db, 'jobs', job.id);

      const reasonObj = reasons.find(r => r.value === reason);

      const notice = {
        notifiedAt: serverTimestamp(),
        installerName: installerName,
        originalDate: job.scheduledDate || serverTimestamp(),
      };

      // Only add fields if they have values
      if (continuationDate) {
        notice.continuationDate = new Date(continuationDate);
      }

      if (reason) {
        notice.reason = reason;
        notice.reasonLabel = reasonObj?.label || reason;
      }

      if (notes.trim()) {
        notice.notes = notes.trim();
      }

      if (workCompleted.trim()) {
        notice.workCompleted = workCompleted.trim();
      }

      if (workRemaining.trim()) {
        notice.workRemaining = workRemaining.trim();
      }

      notice.customerInformed = customerInformed;

      await updateDoc(jobRef, {
        continuationNotices: arrayUnion(notice)
      });

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Error submitting continuation notice:', error);
      alert('Failed to submit notice. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 1000,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '500px',
          margin: '20px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e5e5e5',
          position: 'sticky',
          top: 0,
          background: 'white',
          zIndex: 10,
          borderRadius: '16px 16px 0 0',
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#8B4513',
            margin: '0 0 8px 0',
          }}>
            Let the Office Know
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#666',
            margin: 0,
            lineHeight: '1.5',
          }}>
            All fields are optional - fill out what makes sense
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
          {/* Job Info */}
          <div style={{
            backgroundColor: '#f5f2ed',
            borderRadius: '12px',
            padding: '14px',
            marginBottom: '16px',
          }}>
            <div style={{ fontSize: '15px', fontWeight: '600', color: '#8B4513', marginBottom: '6px' }}>
              Job #{job.contractNumber || job.id?.slice(0, 6)}
            </div>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
              {job.customerName || job.name}
            </div>
            <div style={{ fontSize: '13px', color: '#999' }}>
              {job.address || job.name}
            </div>
          </div>

          {/* Reason */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '15px',
              fontWeight: '600',
              color: '#2b2b2b',
              marginBottom: '8px',
            }}>
              Why couldn't you finish?
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '15px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                background: 'white',
                boxSizing: 'border-box',
                WebkitAppearance: 'none',
                appearance: 'none',
              }}
            >
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {/* Continuation Date */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '15px',
              fontWeight: '600',
              color: '#2b2b2b',
              marginBottom: '8px',
            }}>
              When can you come back?
            </label>
            <input
              type="date"
              value={continuationDate}
              onChange={(e) => setContinuationDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              style={{
                width: '100%',
                maxWidth: '100%',
                padding: '14px',
                fontSize: '15px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                boxSizing: 'border-box',
                WebkitAppearance: 'none',
                MozAppearance: 'textfield',
              }}
            />
          </div>

          {/* Work Remaining */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '15px',
              fontWeight: '600',
              color: '#2b2b2b',
              marginBottom: '8px',
            }}>
              What's left to do?
            </label>
            <textarea
              value={workRemaining}
              onChange={(e) => setWorkRemaining(e.target.value)}
              placeholder="e.g., Trim work remaining - about 3 hours"
              rows={3}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '15px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Work Completed */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '15px',
              fontWeight: '600',
              color: '#2b2b2b',
              marginBottom: '8px',
            }}>
              What you got done today:
            </label>
            <textarea
              value={workCompleted}
              onChange={(e) => setWorkCompleted(e.target.value)}
              placeholder="e.g., All windows installed, exterior sealed"
              rows={3}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '15px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '15px',
              fontWeight: '600',
              color: '#2b2b2b',
              marginBottom: '8px',
            }}>
              Additional notes:
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything else the office should know..."
              rows={3}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '15px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Customer Informed */}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            padding: '14px',
            background: '#f5f2ed',
            borderRadius: '12px',
            cursor: 'pointer',
            marginBottom: '20px',
            WebkitTapHighlightColor: 'transparent',
          }}>
            <input
              type="checkbox"
              checked={customerInformed}
              onChange={(e) => setCustomerInformed(e.target.checked)}
              style={{
                width: '22px',
                height: '22px',
                marginRight: '12px',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: '15px', color: '#2b2b2b' }}>
              I told the customer
            </span>
          </label>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '16px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#666',
                background: 'white',
                border: '2px solid #ddd',
                borderRadius: '12px',
                cursor: 'pointer',
                minHeight: '56px',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              style={{
                flex: 1,
                padding: '16px',
                fontSize: '16px',
                fontWeight: '700',
                color: 'white',
                background: submitting ? '#999' : '#8B4513',
                border: 'none',
                borderRadius: '12px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                minHeight: '56px',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {submitting ? 'Sending...' : 'Let Office Know'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}